import type { PayloadAction } from '@reduxjs/toolkit';
import { useGetPayGroupId } from 'Modulor/hooks';
import type { Dispatch } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import IntegrationCommunication from 'services/integration-communication';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import type { PlatformsViewport } from 'store/models';
import {
  getPlatformsViewportSelector,
  isFrameFitToViewportSelector,
  isInPopUpSelector,
  setPlatformsViewport,
} from 'store/slices/global';
import { notificationsSelector } from 'store/slices/notifications';
import { isValidJSON } from 'utils/validator';

import { modalRoutesMap } from '../Modulor/modalRoutes';
import {
  isWithdrrawalRejectOpened as isWithdrawalRejectOpenedSelector,
  withdrawalRejectCloseTriggerSelector,
} from '../store/slices/withdrawalReject';
import { inIframe, MODAL_HEIGHT_GAP, PLATFORM_MIN_HEIGHT, WIDGET_HEADER_HEIGHT, WIDGET_HEIGHT_GAP } from '../utils';

const setViewPortData = (e: MessageEvent, dispatch: Dispatch<PayloadAction<PlatformsViewport>>) => {
  const { data } = e;
  if (!isValidJSON(data)) {
    return;
  }

  const preparedData = JSON.parse(data);
  if (preparedData.id === 'hostHeight') {
    dispatch(setPlatformsViewport(preparedData.data));
  }
};

const isModalPath = (pathname: string) => modalRoutesMap.some((r) => pathname.endsWith(r.path));

const getAppHeight = (
  { navBarHeight, headerHeight, modalHeight, height, isOpenedInPopup }: Omit<PlatformsViewport, 'pageTop'>,
  applyPlatformHeights?: boolean,
  isModal?: boolean,
) => {
  const rootEl = isModal ? document.getElementById('modal-root') : document.getElementById('root');
  const scrollHeight = rootEl?.scrollHeight ?? 0;
  const rectHeight = rootEl?.getBoundingClientRect().height ?? 0;
  const contentHeight = scrollHeight - rectHeight > 20 ? scrollHeight : rectHeight;
  const openedInIframe = inIframe();
  const platformHeaders = isOpenedInPopup ? navBarHeight : headerHeight + navBarHeight;
  const platformsModalHeight = modalHeight - platformHeaders;
  const contentHeightWithGap = contentHeight + WIDGET_HEIGHT_GAP;

  const platformsViewportHeight = height - platformHeaders;
  const requiredDesktopHeight =
    contentHeight > platformsViewportHeight && !applyPlatformHeights ? contentHeightWithGap : platformsViewportHeight;
  const hasInnerCashier = !!document.querySelector('[title="innerCashier"]');
  const requiredModalHeightInIFrame =
    platformsModalHeight < contentHeightWithGap && !applyPlatformHeights && !hasInnerCashier
      ? contentHeightWithGap
      : platformsModalHeight;
  const requiredHeightInIFrame = isOpenedInPopup
    ? requiredModalHeightInIFrame - (hasInnerCashier ? MODAL_HEIGHT_GAP : 0)
    : requiredDesktopHeight;
  const modalInIframe = isModal && openedInIframe && !isOpenedInPopup;
  if (modalInIframe) return requiredHeightInIFrame + WIDGET_HEADER_HEIGHT;
  return openedInIframe ? requiredHeightInIFrame : contentHeightWithGap;
};

export const useFrameChangeHeight = () => {
  const dispatch = useAppDispatch();
  const payGroupId = useGetPayGroupId();
  const { pathname } = useLocation();
  const {
    modalHeight = PLATFORM_MIN_HEIGHT,
    headerHeight = 0,
    navBarHeight = 0,
    height = PLATFORM_MIN_HEIGHT,
  } = useAppSelector(getPlatformsViewportSelector) || {};
  const isOpenedInPopup = useAppSelector(isInPopUpSelector);
  const isWithdrawalRejectOpened = useAppSelector(isWithdrawalRejectOpenedSelector);
  const withdrawalRejectCloseTrigger = useAppSelector(withdrawalRejectCloseTriggerSelector);
  const notifications = useAppSelector(notificationsSelector(payGroupId));
  const isFrameFitToViewport = useAppSelector(isFrameFitToViewportSelector);

  const lastHeightRef = useRef(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const isModal = isModalPath(pathname);
  // Modals fill the platform viewport and scroll internally (see ModalContent.scss).
  // Their frame height must come from platform values only: measuring modal content that is
  // sized in vh creates a feedback loop with the iframe height (SP-31039).
  const applyPlatformHeights =
    isModal || (!withdrawalRejectCloseTrigger ? isWithdrawalRejectOpened : false) || isFrameFitToViewport;

  useLayoutEffect(() => {
    const handler = (e: MessageEvent) => setViewPortData(e, dispatch);
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [dispatch]);

  useEffect(() => {
    const sendHeight = () => {
      const requiredAppHeight = getAppHeight(
        {
          modalHeight,
          headerHeight,
          navBarHeight,
          height,
          isOpenedInPopup,
        },
        applyPlatformHeights,
        isModal,
      );

      if (Math.abs(requiredAppHeight - lastHeightRef.current) > 2) {
        lastHeightRef.current = requiredAppHeight;
        IntegrationCommunication.sendMessage({
          id: 'frameChangedHeight',
          value: requiredAppHeight,
        });
      }
    };

    // Get the target element based on modal state
    // For lazy modals wrapped in Suspense, #modal-root may not exist yet.
    // Use requestAnimationFrame to defer measurement until the next paint,
    // allowing the modal portal to mount. Fall back to #root if still unavailable.
    let resizeHandler: (() => void) | null = null;
    let rafId: number | null = null;

    const trySetupObserver = () => {
      const root = isModal ? document.getElementById('modal-root') : document.getElementById('root');
      if (!root) {
        if (isModal) {
          // Modal root not yet mounted (lazy/Suspense), retry after next paint
          rafId = requestAnimationFrame(trySetupObserver);
        }
        return;
      }

      sendHeight();

      if (typeof ResizeObserver !== 'undefined') {
        observerRef.current = new ResizeObserver(() => sendHeight());
        observerRef.current.observe(root);
      } else {
        resizeHandler = () => sendHeight();
        window.addEventListener('resize', resizeHandler);
      }
    };

    trySetupObserver();

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observerRef.current?.disconnect();
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler);
      }
    };
  }, [
    pathname,
    notifications,
    modalHeight,
    headerHeight,
    navBarHeight,
    height,
    isOpenedInPopup,
    isWithdrawalRejectOpened,
    withdrawalRejectCloseTrigger,
    isFrameFitToViewport,
    applyPlatformHeights,
  ]);
};
